import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, Fragment, useMemo, useState } from 'react';

import { _getCountries } from '@/api/lookup/queries';
import { LocationList } from '@/api/lookup/types';
import type { SocialPlatform } from '@/api/profile/queries';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import TrashIcon from '@/assets/svg/ui-elements/trash-2.svg';
import { ContactAddressFields } from '@/features/contact-address';
import { PLATFORMS } from '@/pages/settings/modules/my-profile-redesign/public-tab/social-platforms';
import { isLooselyValidPhone } from '@/pages/settings/modules/my-profile-redesign/public-tab/validators';
import { Button, Drawer, IconButton, Input, InputPhone, Select } from '@/shared/design-system';
import { isValidEmail } from '@/utils/validate-email';

import type { SocialProfileKey } from '../../components/social-profiles-block';
import type { ContactData } from '../../types';
import { ContactAutocomplete } from './contact-autocomplete';
import { FieldAnnotation } from './field-annotation';
import {
  AddSectionRow,
  AnnotationSlot,
  FieldRows,
  FieldSection,
  ReviewSections,
  SocialRow,
  ValueRow,
} from './styled';
import { hasAddressValue, useCardMatch } from './use-card-match';
import { useContactSuggestions, useOrganizationSuggestions } from './use-suggestions';

const EMPTY_ADDRESS = { country: '', city: '', street: '', street2: '', state: '', postalCode: '' };

type OptionalSection = 'address' | 'jobTitle' | 'company' | 'website' | 'social';

const ADD_SECTION_BUTTONS: { label: string; section: OptionalSection }[] = [
  { label: 'Add organization', section: 'company' },
  { label: 'Add address', section: 'address' },
  { label: 'Add website', section: 'website' },
  { label: 'Add social media', section: 'social' },
  { label: 'Add job title', section: 'jobTitle' },
];

const SOCIAL_OPTIONS = (Object.keys(PLATFORMS) as SocialPlatform[])
  .filter((platform): platform is SocialProfileKey => platform !== 'Website')
  .map((platform) => ({ value: platform, label: PLATFORMS[platform].label }));

const DEFAULT_SOCIAL_PLATFORM = SOCIAL_OPTIONS[0].value;

function deriveVisibleSections(defaults: Partial<ContactData>): Record<OptionalSection, boolean> {
  return {
    address: Boolean(defaults.addresses?.length),
    jobTitle: Boolean(defaults.jobTitle),
    company: Boolean(defaults.organizationName || defaults.organizationId),
    website: Boolean(defaults.websites?.length),
    social: Boolean(defaults.socialLinks?.length),
  };
}

interface ReviewCardDrawerProps {
  open: boolean;
  defaults: Partial<ContactData>;
  isSubmitting?: boolean;
  onContinue: (_data: Partial<ContactData>) => void;
  onBack: () => void;
  onClose: () => void;
}

export const ReviewCardDrawer = ({
  open,
  defaults,
  isSubmitting,
  onContinue,
  onBack,
  onClose,
}: ReviewCardDrawerProps) => {
  const [data, setData] = useState<Partial<ContactData>>(defaults);
  const [sections, setSections] = useState(() => deriveVisibleSections(defaults));

  const { isAlreadyPresent, isNameKnown, isAddressKnown } = useCardMatch(data);
  const { suggestions: contactSuggestions, isLoading: isSearchingContacts } = useContactSuggestions(
    data.name,
  );
  const { suggestions: organizationSuggestions, isLoading: isSearchingOrganizations } =
    useOrganizationSuggestions(data.organizationName);

  const { data: countries = [], isLoading: isCountriesLoading } = useQuery(
    _getCountries({ list: LocationList.ADDRESS, includeRegions: true }),
  );
  const countryOptions = useMemo(
    () => countries.map((country) => ({ label: country.name, value: country.id })),
    [countries],
  );
  const regionOptionsFor = (countryId: string | null | undefined) =>
    countries
      .find((country) => country.id === countryId)
      ?.regions.map((region) => ({ label: region.name, value: region.id })) ?? [];

  const emails = data.emails?.length ? data.emails : [''];
  const phones = data.phones?.length ? data.phones : [''];
  const websites = data.websites?.length ? data.websites : [''];
  const addresses = data.addresses?.length ? data.addresses : [EMPTY_ADDRESS];
  const socialLinks = data.socialLinks ?? [];

  const patch = (next: Partial<ContactData>) => setData((prev) => ({ ...prev, ...next }));

  const updateAt = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, i) => (i === index ? value : item));
  const removeAt = <T,>(list: T[], index: number): T[] => list.filter((_, i) => i !== index);

  const showSection = (section: OptionalSection) =>
    setSections((prev) => ({ ...prev, [section]: true }));

  const hideSection = (section: OptionalSection, cleared: Partial<ContactData>) => {
    setSections((prev) => ({ ...prev, [section]: false }));
    patch(cleared);
  };

  const hiddenSections = ADD_SECTION_BUTTONS.filter(({ section }) => !sections[section]);

  const emailErrors = emails.map((email) => Boolean(email) && !isValidEmail(email.trim()));
  const phoneErrors = phones.map((phone) => Boolean(phone) && !isLooselyValidPhone(phone));
  const isValid =
    Boolean(data.name?.trim()) && !emailErrors.some(Boolean) && !phoneErrors.some(Boolean);

  const handleContinue = () =>
    onContinue({
      ...data,
      emails: emails.map((email) => email.trim()).filter(Boolean),
      phones: phones.map((phone) => phone.trim()).filter(Boolean),
      websites: websites.map((website) => website.trim()).filter(Boolean),
      socialLinks: socialLinks.filter((link) => link.value.trim()),
      addresses: (data.addresses ?? []).filter(hasAddressValue),
    });

  const deleteButton = (label: string, onClick: () => void) => (
    <IconButton aria-label={label} color="destructive" variant="ghost" size="sm" onClick={onClick}>
      <TrashIcon width={16} height={16} />
    </IconButton>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title="Review contact details"
      description="Check what we read and edit anything before continuing"
      maxWidth={504}
      secondaryAction={{ children: 'Back', color: 'tertiary', onClick: onBack }}
      primaryAction={{
        children: 'Continue',
        onClick: handleContinue,
        disabled: !isValid || isSubmitting,
      }}
    >
      <ReviewSections>
        <ContactAutocomplete
          label="Full Name"
          required
          placeholder="Enter full name"
          value={data.name ?? ''}
          suggestions={contactSuggestions}
          isLoading={isSearchingContacts}
          onChange={(value) => patch({ name: value })}
          onSelect={(suggestion) => patch({ name: suggestion.label })}
          endIcon={data.name?.trim() ? <FieldAnnotation isAlreadyPresent={isNameKnown} /> : null}
        />

        <FieldSection>
          <FieldRows>
            {emails.map((email, index) => (
              <ValueRow key={index}>
                <Input
                  size="lg"
                  label={index === 0 ? 'Email' : undefined}
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  state={emailErrors[index] ? 'destructive' : 'default'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    patch({ emails: updateAt(emails, index, event.target.value) })
                  }
                  endIcon={
                    email.trim() ? (
                      <FieldAnnotation isAlreadyPresent={isAlreadyPresent('Email', email)} />
                    ) : null
                  }
                />
                {index > 0 &&
                  deleteButton(`Remove email ${index + 1}`, () =>
                    patch({ emails: removeAt(emails, index) }),
                  )}
              </ValueRow>
            ))}
          </FieldRows>
          <Button
            variant="subtle"
            color="primary"
            size="sm"
            onClick={() => patch({ emails: [...emails, ''] })}
          >
            <PlusIcon width={16} height={16} />
            Add email
          </Button>
        </FieldSection>

        <FieldSection>
          <FieldRows>
            {phones.map((phone, index) => (
              <ValueRow key={index}>
                <InputPhone
                  label={index === 0 ? 'Phone' : undefined}
                  ariaLabel={`Phone ${index + 1}`}
                  value={phone}
                  placeholder="Enter phone"
                  state={phoneErrors[index] ? 'destructive' : undefined}
                  onChange={({ e164 }) => patch({ phones: updateAt(phones, index, e164) })}
                  endIcon={
                    phone.trim() ? (
                      <FieldAnnotation isAlreadyPresent={isAlreadyPresent('Phone', phone)} />
                    ) : null
                  }
                />
                {index > 0 &&
                  deleteButton(`Remove phone ${index + 1}`, () =>
                    patch({ phones: removeAt(phones, index) }),
                  )}
              </ValueRow>
            ))}
          </FieldRows>
          <Button
            variant="subtle"
            color="primary"
            size="sm"
            onClick={() => patch({ phones: [...phones, ''] })}
          >
            <PlusIcon width={16} height={16} />
            Add phone
          </Button>
        </FieldSection>

        {sections.address && (
          <FieldSection>
            {addresses.map((address, index) => (
              <Fragment key={index}>
                <ValueRow>
                  <Select
                    size="lg"
                    label={index === 0 ? 'Address' : undefined}
                    options={countryOptions}
                    value={address.country || null}
                    placeholder="Enter country"
                    disabled={isCountriesLoading}
                    showSearch
                    onChange={(value) =>
                      patch({
                        addresses: updateAt(addresses, index, {
                          ...address,
                          country: value,
                          state: '',
                        }),
                      })
                    }
                  />
                  {hasAddressValue(address) && (
                    <AnnotationSlot>
                      <FieldAnnotation isAlreadyPresent={isAddressKnown} />
                    </AnnotationSlot>
                  )}
                  {deleteButton(`Remove address ${index + 1}`, () =>
                    addresses.length === 1
                      ? hideSection('address', { addresses: [] })
                      : patch({ addresses: removeAt(addresses, index) }),
                  )}
                </ValueRow>

                <ContactAddressFields
                  address={address}
                  index={index}
                  regionOptions={regionOptionsFor(address.country)}
                  isCountriesLoading={isCountriesLoading}
                  onChange={(next) => patch({ addresses: updateAt(addresses, index, next) })}
                />
              </Fragment>
            ))}
            <Button
              variant="subtle"
              color="primary"
              size="sm"
              onClick={() => patch({ addresses: [...addresses, { ...EMPTY_ADDRESS }] })}
            >
              <PlusIcon width={16} height={16} />
              Add address
            </Button>
          </FieldSection>
        )}

        {sections.jobTitle && (
          <ValueRow>
            <Input
              size="lg"
              label="Job Title"
              placeholder="Enter job title"
              value={data.jobTitle ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                patch({ jobTitle: event.target.value })
              }
              endIcon={
                data.jobTitle?.trim() ? (
                  <FieldAnnotation isAlreadyPresent={isAlreadyPresent('JobTitle', data.jobTitle)} />
                ) : null
              }
            />
            {deleteButton('Remove job title', () =>
              hideSection('jobTitle', { jobTitle: undefined }),
            )}
          </ValueRow>
        )}

        {sections.company && (
          <ValueRow>
            <ContactAutocomplete
              label="Company / Organization"
              placeholder="Enter company"
              value={data.organizationName ?? ''}
              suggestions={organizationSuggestions}
              isLoading={isSearchingOrganizations}
              createNewLabel="Create organization"
              onChange={(value) => patch({ organizationName: value, organizationId: undefined })}
              onSelect={(suggestion) =>
                patch({ organizationName: suggestion.label, organizationId: suggestion.id })
              }
              endIcon={
                data.organizationName?.trim() ? (
                  <FieldAnnotation
                    isAlreadyPresent={isAlreadyPresent('Company', data.organizationName)}
                  />
                ) : null
              }
            />
            {deleteButton('Remove company', () =>
              hideSection('company', { organizationName: undefined, organizationId: undefined }),
            )}
          </ValueRow>
        )}

        {sections.website && (
          <FieldSection>
            <FieldRows>
              {websites.map((website, index) => (
                <ValueRow key={index}>
                  <Input
                    size="lg"
                    label={index === 0 ? 'Website' : undefined}
                    placeholder="Enter website URL"
                    value={website}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      patch({ websites: updateAt(websites, index, event.target.value) })
                    }
                    endIcon={
                      website.trim() ? (
                        <FieldAnnotation isAlreadyPresent={isAlreadyPresent('Website', website)} />
                      ) : null
                    }
                  />
                  {deleteButton(`Remove website ${index + 1}`, () =>
                    websites.length === 1
                      ? hideSection('website', { websites: [] })
                      : patch({ websites: removeAt(websites, index) }),
                  )}
                </ValueRow>
              ))}
            </FieldRows>
            <Button
              variant="subtle"
              color="primary"
              size="sm"
              onClick={() => patch({ websites: [...websites, ''] })}
            >
              <PlusIcon width={16} height={16} />
              Add website
            </Button>
          </FieldSection>
        )}

        {sections.social && (
          <FieldSection>
            <FieldRows>
              {socialLinks.map((link, index) => (
                <SocialRow key={index}>
                  <Select
                    size="lg"
                    options={SOCIAL_OPTIONS}
                    value={link.platform}
                    ariaLabel={`Social platform ${index + 1}`}
                    onChange={(value) =>
                      patch({
                        socialLinks: updateAt(socialLinks, index, {
                          ...link,
                          platform: value as SocialProfileKey,
                        }),
                      })
                    }
                  />
                  <Input
                    size="lg"
                    placeholder={PLATFORMS[link.platform]?.placeholder}
                    value={link.value}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      patch({
                        socialLinks: updateAt(socialLinks, index, {
                          ...link,
                          value: event.target.value,
                        }),
                      })
                    }
                  />
                  {deleteButton(`Remove social media ${index + 1}`, () =>
                    socialLinks.length === 1
                      ? hideSection('social', { socialLinks: [] })
                      : patch({ socialLinks: removeAt(socialLinks, index) }),
                  )}
                </SocialRow>
              ))}
            </FieldRows>
            <Button
              variant="subtle"
              color="primary"
              size="sm"
              onClick={() =>
                patch({
                  socialLinks: [...socialLinks, { platform: DEFAULT_SOCIAL_PLATFORM, value: '' }],
                })
              }
            >
              <PlusIcon width={16} height={16} />
              Add social media
            </Button>
          </FieldSection>
        )}

        {hiddenSections.length > 0 && (
          <AddSectionRow>
            {hiddenSections.map(({ label, section }) => (
              <Button
                key={section}
                variant="subtle"
                color="primary"
                size="sm"
                onClick={() => {
                  showSection(section);
                  if (section === 'social' && socialLinks.length === 0) {
                    patch({
                      socialLinks: [{ platform: DEFAULT_SOCIAL_PLATFORM, value: '' }],
                    });
                  }
                }}
              >
                <PlusIcon width={16} height={16} />
                {label}
              </Button>
            ))}
          </AddSectionRow>
        )}
      </ReviewSections>
    </Drawer>
  );
};
